import { StoreDoc } from '@gooduncles/gu-app-schema';
import { DeliverySpotDoc } from '@gooduncles/gu-app-schema';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import 'ag-grid-enterprise';
import { ColDef, NewValueParams } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { Button, Popconfirm, message, notification } from 'antd';
import { BaseOptionType } from 'antd/es/select';
import { FC, forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';

import {
  getCellStyleForLatestOrderAt,
  onCellValueChangedWithUpdate,
  onValueSetterWithValidation,
  stringDateFormatter,
} from 'src/utils/ag-grid-util';
import { errorObjectToString } from 'src/utils/common';
import { onDeliverySpotRowDragEnd, onDeliverySpotSortKeyChange } from 'src/utils/delivery-route-util';
import { FirebaseManager } from 'src/utils/firebase/firebase-manager';
import {
  deleteDeliverySpot,
  getDeliverySpotEditStamp,
  getStores,
  updateDeliverySpot,
  updateStore,
} from 'src/utils/firebase/firebase-short-cut';

import { selectValidPartnersUsers } from 'src/redux/slices/partners';
import { useAppSelector } from 'src/redux/store';

import DownloadRawDataCell from 'src/atomic-components/atoms/AgCell/AgCellDownloadRawData';
import AgCellToggleEditor from 'src/atomic-components/atoms/AgCell/AgCellToggleEditor';
import SelectableCell from 'src/components/Common/SelectableCell/SelectableCell';

const firebaseManager = FirebaseManager.getInstance();

const UNDEFINED_PARTNER_SORT_KEY = 9999;
const PARTNER_CHANGE_SORT_KEY_OFFSET = 1000;

export type DeliverySpotWithShopRowData = DeliverySpotDoc & {
  stores: StoreDoc[];
  partnerSortKey: number;
};

/**
 * 배송지 삭제 버튼
 * : 배송지 삭제시 해당 배송지를 사용하는 매장의 배송지를 'unregistered'로 변경해야한다.
 */
const DeleteDeliverySpotButton: FC<{ spotId: string }> = ({ spotId }) => {
  const [loading, setLoading] = useState(false);
  const onDeleteDeliverySpot = useCallback(async () => {
    setLoading(true);
    try {
      const stores = await getStores([['deliverySpotId', '==', spotId]]);
      firebaseManager.batchStart();
      for (const store of stores) {
        await updateStore(store._id, { deliverySpotId: 'unregistered' }, { bBatch: true });
      }
      await deleteDeliverySpot(spotId, { bBatch: true });
      await firebaseManager.batchEnd();
    } catch (error) {
      console.error(error);
      const description = errorObjectToString(error);
      notification.error({
        message: '배송지 삭제에 실패했습니다.',
        description,
      });
    }
    setLoading(false);
  }, [spotId]);

  return (
    <Popconfirm
      title='선택한 배송지점을 삭제합니다.'
      onConfirm={onDeleteDeliverySpot}
      okButtonProps={{
        loading,
      }}
    >
      <Button danger>삭제</Button>
    </Popconfirm>
  );
};

const onCellValueChanged = onCellValueChangedWithUpdate<DeliverySpotWithShopRowData>('배송지 관리');
const deliverySpotPath = 'deliverySpot';

/** 좌표는 표시 필드(updatedAt·updatedBy)와 한 번에 써야 docVersion 이 직원 수정으로 판별한다. */
const onCoordChanged = async (params: NewValueParams<DeliverySpotWithShopRowData>) => {
  const field = params.colDef.field as 'lat' | 'lng';
  const newValue = params.data[field];
  if (newValue === params.oldValue) return;
  try {
    await updateDeliverySpot(params.data._id, { [field]: newValue, ...(await getDeliverySpotEditStamp()) });
    message.success('변경완료');
  } catch (error) {
    console.error(error);
    message.error(errorObjectToString(error));
  }
};

const defaultColDef: ColDef<DeliverySpotWithShopRowData> = {
  sortable: true,
  resizable: true,
  filter: true,
};

type DeliverySpotWithShopTableProps = {
  rowData: DeliverySpotWithShopRowData[];
  enableDragAndDrop?: boolean;
  onSelectionChanged?: (selectedIds: string[]) => void;
};

export type DeliverySpotWithShopTableRef = {
  clearSelection: () => void;
};

const DeliverySpotWithShopTable = forwardRef<DeliverySpotWithShopTableRef, DeliverySpotWithShopTableProps>(
  ({ rowData, enableDragAndDrop = false, onSelectionChanged }, ref) => {
    const gridRef = useRef<AgGridReact<DeliverySpotWithShopRowData>>(null);
    const partnersUsers = useAppSelector(selectValidPartnersUsers);
    const partnerList: BaseOptionType[] = useMemo(
      () => [
        ...partnersUsers.map((user) => ({
          label: `${user.deliveryPartnerStatus.isActive ? '🟢' : '🔴'} ${user.name} - ${
            user.deliveryPartnerStatus.sortKey
          }`,
          value: user._id,
          sortKey: user.deliveryPartnerStatus.sortKey,
        })),
        {
          label: '⚪️ 미지정',
          value: 'undefined',
          sortKey: UNDEFINED_PARTNER_SORT_KEY,
        },
      ],
      [partnersUsers],
    );

    // rowData 에 의존하면 스냅샷마다 columnDefs 가 새로 만들어져 그리드 전체가 다시 그려진다.
    const onUpdatePartner = useCallback(
      (spotId: string, currentSortKey: number) => async (partnerId: string) => {
        try {
          const newSortKey = currentSortKey + PARTNER_CHANGE_SORT_KEY_OFFSET; // 기존 순서에 offset 추가

          await updateDeliverySpot(spotId, {
            partnerId,
            sortKey: newSortKey,
            sortKeyForBadge: newSortKey,
          });
        } catch (error) {
          console.error(error);
          const description = errorObjectToString(error);
          notification.error({
            message: '담당 파트너 변경에 실패했습니다.',
            description,
          });
        }
      },
      [],
    );

    useImperativeHandle(ref, () => ({
      clearSelection: () => {
        const api = gridRef.current?.api;
        if (api) {
          api.deselectAll();
        }
      },
    }));

    const onRowSelectionChanged = useCallback(
      (event: any) => {
        const selectedRows = event.api.getSelectedRows();
        const selectedIds = selectedRows.map((row: DeliverySpotWithShopRowData) => row._id);
        onSelectionChanged?.(selectedIds);
      },
      [onSelectionChanged],
    );

    const columnDefs: ColDef<DeliverySpotWithShopRowData>[] = useMemo(
      () => [
        {
          field: '_id',
          hide: true,
        },
        {
          width: 50,
          headerName: '',
          headerCheckboxSelection: true,
          checkboxSelection: true,
          pinned: 'left',
          lockPosition: true,
          suppressMenu: true,
          sortable: false,
          filter: false,
          resizable: false,
        },
        {
          width: 100,
          field: 'sortKey',
          headerName: '순서',
          editable: true,
          cellEditor: 'agTextCellEditor',
          valueSetter: (params) => onValueSetterWithValidation(params, ['_id'], undefined, { type: 'number' }),
          onCellValueChanged: onDeliverySpotSortKeyChange,
          ...(enableDragAndDrop && { rowDrag: true }),
        },
        {
          width: 100,
          field: 'sortKeyForBadge',
          headerName: '순서',
        },
        {
          width: 280,
          field: 'title',
          headerName: '배송지명',
          valueFormatter: (params) => {
            const length = params?.data?.stores.length ?? 0;
            return `${params.value} (${length})`;
          },
          cellRenderer: 'agGroupCellRenderer',
          editable: true,
          cellEditor: 'agTextCellEditor',
          valueSetter: (params) => onValueSetterWithValidation(params, ['_id']),
          onCellValueChanged: (params) => onCellValueChanged(params, deliverySpotPath, params.data._id),
        },
        {
          width: 220,
          field: 'partnerId',
          headerName: '담당 파트너',
          cellRenderer: (params: any) => {
            const partnerId = params.value;
            const data = params.data as DeliverySpotWithShopRowData;
            const sortedList = partnerList.sort((a, b) => a.sortKey - b.sortKey);
            return (
              <SelectableCell
                options={sortedList}
                value={partnerId}
                onChange={onUpdatePartner(data._id, data.sortKey || 0)}
              />
            );
          },
          filterParams: {
            valueFormatter: (params: any) => {
              const value = params.value;
              return partnerList.find((option) => option.value === value)?.label ?? value;
            },
          },
        },
        {
          width: 280,
          field: 'roadAddress',
          headerName: '도로명 주소',
          editable: true,
          cellEditor: 'agTextCellEditor',
          valueSetter: (params) => onValueSetterWithValidation(params, ['_id']),
          onCellValueChanged: (params) => onCellValueChanged(params, deliverySpotPath, params.data._id),
        },
        {
          width: 240,
          field: 'jibunAddress',
          headerName: '지번 주소',
          editable: true,
          cellEditor: 'agTextCellEditor',
          valueSetter: (params) => onValueSetterWithValidation(params, ['_id']),
          onCellValueChanged: (params) => onCellValueChanged(params, deliverySpotPath, params.data._id),
        },
        {
          width: 150,
          field: 'stores',
          headerName: '최근 주문일(종합)',
          valueGetter: (params) => {
            const latestOrderDates = params.data
              ? params.data.stores
                  .filter((store) => store.latestOrderAt)
                  .map((store) => new Date(store.latestOrderAt as string))
              : [];
            return latestOrderDates.length
              ? new Date(Math.max(...latestOrderDates.map((date) => date.getTime())))
              : null;
          },
          valueFormatter: stringDateFormatter,
          cellStyle: getCellStyleForLatestOrderAt,
        },
        {
          width: 114,
          field: 'hidden',
          headerName: '숨김',
          cellRenderer: (params: any) => {
            return (
              <AgCellToggleEditor
                id={params.data._id}
                value={params.value}
                field={params.colDef.field}
                onChange={updateDeliverySpot}
              />
            );
          },
        },
        {
          width: 114,
          field: 'lat',
          headerName: '위도',
          editable: true,
          valueSetter: (params) => onValueSetterWithValidation(params, ['_id'], undefined, { type: 'number' }),
          onCellValueChanged: onCoordChanged,
        },
        {
          width: 114,
          field: 'lng',
          headerName: '경도',
          editable: true,
          valueSetter: (params) => onValueSetterWithValidation(params, ['_id'], undefined, { type: 'number' }),
          onCellValueChanged: onCoordChanged,
        },
        {
          width: 96,
          field: '_id',
          headerName: '삭제',
          cellRenderer: (params: any) => {
            const spotId = params.value;
            return <DeleteDeliverySpotButton spotId={spotId} />;
          },
        },
        {
          headerName: 'raw data',
          hide: true,
          cellRenderer: DownloadRawDataCell,
        },
      ],
      [onUpdatePartner, partnerList, enableDragAndDrop],
    );

    const detailCellRendererParams = useMemo(() => {
      const detailColumnDefs: ColDef<StoreDoc>[] = [
        {
          field: '_id',
          hide: true,
        },
        {
          width: 320,
          field: 'storeNickname',
          headerName: '매장명',
        },
        {
          field: 'latestOrderAt',
          headerName: '최근 주문일',
          width: 120,
          cellStyle: getCellStyleForLatestOrderAt,
          valueFormatter: stringDateFormatter,
        },
        {
          width: 240,
          field: 'address',
          headerName: '주소',
        },
        {
          field: 'addressDetail',
          headerName: '상세주소',
        },
        {
          width: 100,
          field: 'sido',
          headerName: '시도',
        },
        {
          width: 120,
          field: 'sigungu',
          headerName: '시군구',
        },
        {
          width: 120,
          field: 'lat',
          headerName: '위도',
        },
        {
          width: 120,
          field: 'lng',
          headerName: '경도',
        },
      ];

      return {
        refreshStrategy: 'rows',
        detailGridOptions: {
          columnDefs: detailColumnDefs,
          defaultColDef,
          enableCellChangeFlash: true,
        },
        getDetailRowData: (params: any) => {
          const row = params.data as DeliverySpotWithShopRowData;
          const data = row.stores;
          params.successCallback(data);
        },
      };
    }, []);

    return (
      <div className='ag-theme-alpine height100'>
        <AgGridReact
          ref={gridRef}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          rowData={rowData}
          // rowId를 지정해주지 않으면 데이터 변경시 refresh가 발생한다.
          getRowId={(params) => params.data._id}
          // 편집 완료후 스크롤 이동을 막는다.
          suppressScrollOnNewData={true}
          // 그룹 노드
          masterDetail={true}
          keepDetailRows={true}
          detailRowAutoHeight={true}
          detailCellRendererParams={detailCellRendererParams}
          // 선택 관련 설정
          rowSelection='multiple'
          onSelectionChanged={onRowSelectionChanged}
          suppressRowClickSelection={enableDragAndDrop}
          // DnD 관련 설정 (파트너별 탭에서만 활성화)
          {...(enableDragAndDrop && {
            rowDragManaged: true,
            rowDragMultiRow: true,
            animateRows: true,
            onRowDragEnd: onDeliverySpotRowDragEnd,
            suppressMoveWhenRowDragging: true,
          })}
        />
      </div>
    );
  },
);

DeliverySpotWithShopTable.displayName = 'DeliverySpotWithShopTable';

export default DeliverySpotWithShopTable;
